import React, {useState, useEffect, useCallback} from 'react';
import {Field, Form} from 'react-final-form';
import t from 'babel-plugin-yate/macro';
import {useDispatch} from 'react-redux';
import {addModal} from '../../store/app';
import {
  postCheckValidatedEmail,
  postValidateEmail,
  postValidateEmailCode,
} from '../../store/request';
import {
  composeFormValidators,
  hasMaxLength,
  isAlphaNumeric,
  isEmail,
  isRequired,
} from '../../lib/validations';
import {formatWhiteSpaceFormField} from '../../lib/misc';

import ValidationError from '../../components/Form/ValidationError';
import {Input} from '../../components/Form/Input';
import Button from '../../components/Button';
import Icon from '../../components/Icons';
import Modal from '../../components/Modal';

let timerId;

function Email(props) {
  const {
    uuid,
    handlerSetIsEmailValidated,
    customer,
    disableValidationBtn,
    disabled,
  } = props;
  const {identity_card, email} = customer;
  const {cnp} = identity_card;
  const dispatch = useDispatch();
  const [disableGenerateBtn, setDisableGenerateBtn] = useState(false);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const [codeValidationFailed, setCodeValidationFailed] = useState(null);
  const [codeGenerationFailed, setCodeGenerationFailed] = useState(null);
  const [validatedEmailCNP, setValidatedEmail] = useState(undefined);
  const [checkedEmail, setCheckedEmail] = useState(undefined);
  const [isModalOpen, setModalOpen] = useState(false);
  const emailCNP = email + cnp;

  const updateValidatedEmail = useCallback(() => {
    setValidatedEmail(emailCNP);
    setModalOpen(false);
    handlerSetIsEmailValidated(true);
  }, [handlerSetIsEmailValidated, emailCNP]);

  const handleValidateEmail = useCallback(async () => {
    const res = await dispatch(postCheckValidatedEmail(uuid, {email, cnp}));

    if (res.error) {
      dispatch(addModal('application_email_validation'));
      setModalOpen(true);

      if (checkedEmail !== email) {
        setCodeGenerationFailed(null);
        setDisableGenerateBtn(false);
      }
      return;
    }

    updateValidatedEmail();
  }, [dispatch, uuid, email, cnp, checkedEmail, updateValidatedEmail]);

  const handleGenerateCode = useCallback(async () => {
    setDisableGenerateBtn(true);
    setCodeGenerationFailed(null);
    setCheckedEmail(email);
    setIsWaitingResponse(true);
    const res = await dispatch(postValidateEmail(uuid, {email, cnp}));
    setIsWaitingResponse(false);

    if (res.error) {
      setCodeGenerationFailed(res.errorMessage);
    }
  }, [dispatch, uuid, email, cnp]);

  const handleValidateCode = useCallback(
    async ({code}) => {
      setCodeValidationFailed(null);
      setIsWaitingResponse(true);
      const res = await dispatch(
        postValidateEmailCode(uuid, {email, cnp, code}),
      );
      setIsWaitingResponse(false);

      if (res.error) {
        setCodeValidationFailed(res.errorMessage);
        return;
      }

      updateValidatedEmail();
    },
    [dispatch, uuid, email, cnp, updateValidatedEmail],
  );

  useEffect(() => {
    if (disableGenerateBtn === false) {
      return;
    }

    clearTimeout(timerId);
    timerId = setTimeout(() => {
      setDisableGenerateBtn(false);
      setCodeGenerationFailed(null);
      setCheckedEmail(undefined);
    }, 120 * 1000);

    return () => clearTimeout(timerId);
  }, [disableGenerateBtn]);

  useEffect(() => {
    handlerSetIsEmailValidated(validatedEmailCNP === emailCNP);
  }, [handlerSetIsEmailValidated, emailCNP, validatedEmailCNP]);

  return (
    <div className="flex flex-wrap w-full relative">
      <div className="flex-grow">
        <Field
          name="customer.email"
          validate={composeFormValidators(
            isEmail(),
            isRequired(),
            hasMaxLength(255),
          )}
          label={t`Email address`}
          component={Input}
          disabled={disabled}
          initialValue={email}
        />
      </div>

      <div className="ml-2 mt-5">
        {validatedEmailCNP === emailCNP ? (
          <div className="flex items-center">
            <Icon name="Checked" width={30} height={40} fill="fill-green" />
            <div className="text-grey-darker-1 pl-2 text-12">{t`Validated`}</div>
          </div>
        ) : (
          <Button
            title={t`Validate`}
            icon={true}
            iconSrc={<Icon name="FileboardChecklist" />}
            customProps={{
              type: 'button',
              disabled: disableValidationBtn,
            }}
            iconPosition="left"
            onClick={handleValidateEmail}
            color="white"
            border={true}
          />
        )}
      </div>

      {/* Field bellow is used to warn the user
      if the email was not validated, when submitting the big form */}
      <Field name="customer.email_validation_warn">
        {({input, meta}) => (
          <>
            <input className="w-0 h-0 absolute invisible" {...input} />

            {meta.error && meta.touched && (
              <div className="w-full">
                <ValidationError value={meta.error} />
              </div>
            )}
          </>
        )}
      </Field>

      {isModalOpen && (
        <Modal
          id="application_email_validation"
          header={t`Validare email`}
          footer={null}
          width="md:w-3/5 lg:w-2/5"
          reqCloseCallback={() => {
            setModalOpen(false);
          }}>
          <Form
            keepDirtyOnReinitialize={true}
            onSubmit={handleValidateCode}
            render={({values, handleSubmit}) => (
              <form onSubmit={handleSubmit} className="w-full p-4">
                <div className="md:flex w-full">
                  <div className="mb-4 md:w-1/2">
                    <Field
                      name="email"
                      label={t`Email address`}
                      component={Input}
                      disabled={true}
                      initialValue={email}
                    />

                    {codeGenerationFailed && (
                      <ValidationError value={codeGenerationFailed} />
                    )}

                    <div
                      className="pt-2"
                      title={t`Puteti trimite un nou cod peste 2 min`}>
                      <Button
                        title={t`Genereaza cod`}
                        customProps={{
                          type: 'button',
                          disabled: isWaitingResponse || disableGenerateBtn,
                        }}
                        onClick={handleGenerateCode}
                        color="blue"
                        border={false}
                      />
                    </div>
                  </div>

                  <div className="md:pl-4 md:w-1/2">
                    <Field
                      autoComplete="one-time-code"
                      label={t`Introduceti codul primit prin Email`}
                      name="code"
                      component={Input}
                      format={formatWhiteSpaceFormField}
                      validate={composeFormValidators(
                        isRequired(),
                        isAlphaNumeric(),
                      )}
                    />

                    {codeValidationFailed && (
                      <ValidationError value={codeValidationFailed} />
                    )}

                    <div className="pt-2">
                      <Button
                        title={t`Validate`}
                        customProps={{
                          type: 'button',
                          disabled: !values.code,
                        }}
                        onClick={() => handleValidateCode(values)}
                        color="green"
                        border={false}
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          />
        </Modal>
      )}
    </div>
  );
}

export default Email;
